// ─────────────────────────────────────────────────────────────────────────────
// navigation.ts — WHERE THE OPERATOR IS, as a plain value.
//
// THE FAULT THIS EXISTS TO CLOSE: MissionControl unmounts when the app switches
// to the agent/chat screen and remounts when it comes back. Any position held in
// its own useState is therefore DESTROYED by a trip through chat, and the surface
// reopened at its default — the domain landing — no matter where the operator
// left. Chat is a door out and back, not a reset.
//
// This is the same family as the landing-tab fault (state defaulted rather than
// preserved) one level out: there it was defaulted per render, here per mount.
// The cure is the same in kind — the position is DERIVED FROM A VALUE THAT
// OUTLIVES THE THING DISPLAYING IT, so it cannot be lost by unmounting.
//
// So the position lives ABOVE the unmount boundary (App.tsx) and arrives as a
// prop. These helpers are pure functions on that value: no React, no component
// state, no side effects — which is also what makes the round trip assertable in
// a plain script rather than by eye.
// ─────────────────────────────────────────────────────────────────────────────

import { SURFACE } from './surface';
import { ROLE_ROOT } from './presentation';

/** Everything needed to put the operator back exactly where they were. */
export interface Nav {
  /** The declared root key. */
  root: string;
  /** The sub-tab key, or null to take the root's first. */
  sub: string | null;
  /** The nested view key (the attached Admin Agent's own views), or null. */
  view: string | null;
  /** The selected tenant for a parameterised root, by registry agent id. */
  tenant: string | null;
}

/** The surface's opening position: the first declared root, nothing chosen yet. */
export const INITIAL_NAV: Nav = { root: SURFACE[0].key, sub: null, view: null, tenant: null };

/** Minimal shape this module needs of a registry agent — NOT the full DTO, so a
 *  change to the projection cannot silently break navigation. */
export interface NavAgent {
  id: string;
  role?: { id: string; label: string } | null;
}

/**
 * WHICH ROOT AN AGENT'S PAGE IS, from its DECLARED role. Null when the role
 * declares no root — that agent has no page of its own and is not given one.
 * This is the single decision the rail makes; the acceptance script drives this
 * exact function rather than a copy of its logic.
 */
export function rootForAgent(a: NavAgent): string | null {
  const id = a.role?.id;
  if (!id) return null;
  if (id === 'receptionist') return 'company_agents';
  return ROLE_ROOT[id] || null;
}

/** Move to a root. A root change clears the sub-tab and view, never the tenant —
 *  the tenant is who you are looking at, not where you are. */
export function goRoot(nav: Nav, root: string): Nav {
  return { ...nav, root, sub: null, view: null };
}

/** Choose a sub-tab within the current root. Changing tab clears the view. */
export function goSub(nav: Nav, sub: string): Nav {
  return { ...nav, sub, view: null };
}

/** Choose a nested view within the current sub-tab. */
export function goView(nav: Nav, view: string): Nav {
  return { ...nav, view };
}

/** Open an agent's own page. A controller lands on its root; a receptionist
 *  lands on Company Agents parameterised to itself. Returns null when the agent
 *  declares no root — the caller then opens its chat instead of inventing a page. */
export function openAgent(nav: Nav, a: NavAgent): Nav | null {
  const root = rootForAgent(a);
  if (!root) return null;
  const tenant = a.role?.id === 'receptionist' ? a.id : null;
  return { root, sub: null, view: null, tenant };
}

/** Open a receptionist's ATTACHED ADMIN AGENT — its own surface and views, on
 *  the same tenant. */
export function openAttachedAdmin(nav: Nav, a: NavAgent): Nav {
  return { root: 'company_agents', sub: 'admin_agent', view: null, tenant: a.id };
}

/**
 * THE ROUND TRIP. Leaving for chat and coming back must be the identity on the
 * position — this pair exists so the property is written down in the code the
 * app uses, not only asserted in a test.
 */
export function leaveForChat(nav: Nav): Nav { return nav; }
export function returnFromChat(nav: Nav): Nav { return nav; }
